import { useLocation } from 'react-router-dom'
import { useContext, useEffect, useMemo } from 'react'
import clsx from 'clsx'
import FooterRoutes from './FooterRoutes'
import App from 'components/App/App'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import GlobalStyles from 'styles/GlobalStyles'
import styled from 'styled-components'
import BannerRoutes from './BannerRoutes'
import NotificationBannerRoutes from './NotificationBannerRoutes'
import ModalPopupsGate from '../ModalPopups/ModalPopupsGate'
import GhaLoadingDiagnostics from 'components/Common/GhaLoadingDiagnostics'
import HeaderRoutes from './HeaderRoutes'
import PageRoutes from './PageRoutes'
import { connect } from 'react-redux'
import GeoContext from 'contexts/geoContext'
import { fetchGeoAirports, fetchGeoState } from 'actions/GeoActions'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ErrorPage from 'components/Pages/ErrorPage'
import { useAppDispatch } from 'hooks/reduxHooks'
import GlobalSearchV2 from 'components/SearchV2/GlobalSearchV2'
import { fetchDepositConfig } from 'actions/SystemActions'
import SupportAssistantChatWidgetRoutes from 'supportAssistant/routes/SupportAssistantChatWidgetRoutes'
import AccountAccessPrompt from 'components/Account/AccountAccessPrompt/AccountAccessPrompt'
import UnhandledErrorPage from 'components/Pages/ErrorPage/UnhandledErrorPage'
import config from 'constants/config'
import { useRestoreCartState } from 'checkout/hooks/useRestoreCartState'
import PromoDisplayPrompt from 'marketing/components/PromoDisplayPrompt'
import ReferralCodePrompt from 'marketing/components/ReferralCodePrompt'
import VoucherPopupPrompt from 'marketing/components/VoucherPopupPrompt'
import usePushNotificationPermission from 'hooks/Notifications/usePushNotificationPermission'
import loadable from '@loadable/component'
import getOverridePromoDisplayPrompt from 'marketing/components/getOverridePromoDisplayPrompt'
import useHeadlessModeBodyClass from 'hooks/useHeadlessModeBodyClass'
import usePaletteVersionBodyClass from 'hooks/usePaletteVersionBodyClass'
import { getRegionCodeFromPathname } from 'lib/string/regionUtils'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import { useIntl } from 'react-intl'
import AuthBackedLocalePreferenceSync from './AuthBackedLocalePreferenceSync'
import { AssistantSurfaceLayoutProvider } from 'tripPlanner/assistant/layout/AssistantSurfaceLayoutContext'
import ExperimentControlPanelLauncher from 'components/DevTools/ExperimentControlPanel/ExperimentControlPanelLauncher'

const SpoofWarning = loadable(() => import('../SpoofWarning'))
const SpoofLaunchpad = loadable(() => import('../SpoofLaunchpad/SpoofLaunchpad'))
const ChannelMarkupStaffBanner = loadable(() => import('../ChannelMarkupStaffBanner/ChannelMarkupStaffBanner'))
const SplashPage = loadable(() => import('components/Pages/SplashPage'))

const fixedHeightRoutes = [
  '/:regionCode/search/map',
  '/:regionCode/search/store/map',
  '/:regionCode/search/homes-and-villas/map',
  '/:regionCode/search/experiences/map',
  '/:regionCode/support',
]

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(var(---vh) * 100);

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    overflow-y: auto;
  }

  &.fixed-height {
    /*
      For pages such as map views where there's no page scrolling.
      Using position: absolute and height: 100%, rather than e.g. height: 100vh
      helps it work better with mobile browsers that cover part of the viewport
      with menu bars. I'm looking at YOU, iOS Safari and iOS Chrome >:(
    */
    position: absolute;
    min-height: 1px;
    height: 100%;
    width: 100%;
  }
`

interface Props {
  pathname: string
  headlessMode: boolean
  isSpoofed?: boolean
  showSpoofLaunchpad: boolean
}

function Routes(props: Props) {
  const { isSpoofed, headlessMode, pathname, showSpoofLaunchpad } = props
  const geo = useContext(GeoContext)
  const intl = useIntl()

  const dispatch = useAppDispatch()
  const isFixedHeightRoute = useMemo(() => {
    return fixedHeightRoutes.some(route => matchLocaleAwarePath(pathname, route))
  }, [pathname])
  const routeRegionCode = useMemo(() => getRegionCodeFromPathname(pathname) ?? '', [pathname])
  const regionCodeToLoad = routeRegionCode || geo.currentRegionCode || config.defaultRegion

  useRestoreCartState()
  usePushNotificationPermission()
  useHeadlessModeBodyClass(headlessMode)
  usePaletteVersionBodyClass()

  useEffect(() => {
    dispatch(fetchGeoState(regionCodeToLoad, true))
    dispatch(fetchGeoAirports())
    dispatch(fetchDepositConfig())
  }, [dispatch, regionCodeToLoad])

  const location = useLocation()
  const overridePromoDisplayPrompt = getOverridePromoDisplayPrompt(intl, pathname, location.search)
  const showSpoofLaunchpadPanel = isSpoofed && showSpoofLaunchpad
  const isSplashPage = !!matchLocaleAwarePath(pathname, '/:regionCode/splashpage')
  const isRegionRoute = !!matchLocaleAwarePath(pathname, '/:regionCode')

  return (
    <ErrorBoundary>
      <AssistantSurfaceLayoutProvider>
        <AuthBackedLocalePreferenceSync />
        <ExperimentControlPanelLauncher />
        <AppWrapper className={clsx({ 'fixed-height': isFixedHeightRoute })}>
          {isSplashPage && <SplashPage />}
          {!isSplashPage && <>
            {!geo.initialised && <LoadingIndicator />}
            {geo.initialised && <>
              {geo.fetchRegionError && <ErrorPage error={geo.fetchRegionError} />}
              {!geo.fetchRegionError && <>
                {isSpoofed && <SpoofWarning />}
                <ChannelMarkupStaffBanner />
                <App>
                  {!headlessMode && isRegionRoute && <BannerRoutes />}
                  {(config.notification && !headlessMode && isRegionRoute) && <NotificationBannerRoutes />}
                  {!headlessMode && isRegionRoute && <HeaderRoutes />}
                  {showSpoofLaunchpadPanel && <SpoofLaunchpad />}
                  {!headlessMode && <ReferralCodePrompt />}
                  {!headlessMode && <ModalPopupsGate />}
                  {!headlessMode && <PromoDisplayPrompt overridePromoDisplayPrompt={overridePromoDisplayPrompt} />}
                  <VoucherPopupPrompt />
                  {!headlessMode && !config.ENABLE_ACCOUNT_ACCESS_PROMPT && <AccountAccessPrompt />}
                  <ErrorBoundary fallback={() => <UnhandledErrorPage />}>
                    {isRegionRoute && <PageRoutes />}
                  </ErrorBoundary>
                </App>
                {!headlessMode && isRegionRoute && <FooterRoutes />}
                {/* This needs to be top level so mobile search has the components available in headless mode */}
                <GlobalSearchV2 />
                {isRegionRoute && <SupportAssistantChatWidgetRoutes />}
              </>}
            </>}
          </>}
          {/* Outside load-state branches so it can report on them. */}
          <GhaLoadingDiagnostics key={pathname} />
          <GlobalStyles />
        </AppWrapper>
      </AssistantSurfaceLayoutProvider>
    </ErrorBoundary>
  )
}

function mapStateToProps(state: App.State) {
  return {
    pathname: state.router.location.pathname,
    headlessMode: state.system.headlessMode,
    isSpoofed: state.auth.account.isSpoofed,
    showSpoofLaunchpad: state.system.showSpoofLaunchpad,
  }
}

export default connect(mapStateToProps)(Routes)
